/**
 * 管理员后台 - API 封装
 * 独立的管理员认证和管理接口
 */

import axios, { type AxiosInstance, type InternalAxiosRequestConfig, type AxiosResponse } from 'axios';
import { message } from 'ant-design-vue';
import type { ApiResponse } from '@shared/types';
import type { MonitorOverview, MonitorSoundEffectResponse, MonitorTrendResponse } from '@shared/domain/adminMonitor';
import type { AdminInfo, AdminLoginParams } from '@/stores/admin';

// 创建管理员专用的 axios 实例
const adminInstance: AxiosInstance = axios.create({
  baseURL: '/api/csdadmin',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json'
  }
});

// 请求拦截器 - 添加管理员Token
adminInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  error => {
    console.error('请求配置错误:', error);
    return Promise.reject(error);
  }
);

// 响应拦截器
adminInstance.interceptors.response.use(
  (response: AxiosResponse<ApiResponse>): any => {
    const { data } = response;

    // 优先检查响应体中的code字段，而不是HTTP状态码
    // 因为后端中间件可能设置了错误的HTTP状态码，但响应体是正确的
    if (data.code !== 0) {
      // 401 未授权，清除 token（不在这里重定向，由路由守卫处理）
      if (data.code === 401) {
        localStorage.removeItem('adminToken');
        return Promise.reject(new Error(data.message || '未授权'));
      }
      
      // 403 无权限
      if (data.code === 403) {
        message.error('无权限访问此功能');
        return Promise.reject(new Error(data.message));
      }
      
      message.error(data.message || '请求失败');
      return Promise.reject(new Error(data.message));
    }

    // 正常响应：返回 ApiResponse 对象（包含 code、message、data、timestamp）
    return data;
  },
  error => {
    // 检查是否有响应体，如果响应体中code为0，则认为请求成功
    // 这是为了处理后端中间件设置了错误HTTP状态码但响应体正确的情况
    if (error.response?.data?.code === 0) {
      console.log('[Admin API] 容错处理：HTTP状态码错误但响应体正确', {
        httpStatus: error.response.status,
        responseCode: error.response.data.code,
        message: error.response.data.message
      });
      
      // 容错响应：直接返回响应体数据（ApiResponse对象）
      // 注意：直接返回数据，不使用Promise.resolve，这样axios会认为请求成功
      return error.response.data;
    }
    
    if (!error.response) {
      message.error('网络连接失败');
    } else {
      const status = error.response.status;
      const data = error.response.data;
      
      // 如果响应体中有code字段，优先使用它
      if (data?.code === 401) {
        localStorage.removeItem('adminToken');
      } else if (data?.code === 403) {
        message.error('无权限访问');
      } else if (status === 401) {
        // 清除 token（不在这里重定向，由路由守卫处理）
        localStorage.removeItem('adminToken');
      } else if (status === 403) {
        message.error('无权限访问');
      } else {
        message.error(data?.message || `请求失败 (${status})`);
      }
    }
    return Promise.reject(error);
  }
);

// ============================================
// 通用请求方法
// ============================================

export async function get<T>(url: string, params?: Record<string, unknown>): Promise<T> {
  try {
    // adminInstance.get() 经过响应拦截器处理后，返回 ApiResponse<T> 对象
    const apiResponse = await adminInstance.get(url, { params }) as unknown as ApiResponse<T>;
    
    // 检查是否是有效的ApiResponse对象
    if (!apiResponse || typeof apiResponse !== 'object' || !('code' in apiResponse)) {
      console.error('[Admin API get] 响应格式错误，不是ApiResponse对象:', apiResponse);
      throw new Error('响应格式错误');
    }
    
    return apiResponse.data;
  } catch (error: any) {
    // 如果axios抛出错误，但响应体是正确的（容错逻辑已处理）
    // 这种情况下error本身就是ApiResponse对象
    if (error && typeof error === 'object' && 'code' in error && error.code === 0) {
      console.log('[Admin API get] 从错误对象中提取数据');
      return error.data;
    }
    throw error;
  }
}

export async function post<T>(url: string, data?: Record<string, unknown>): Promise<T> {
  const response = await adminInstance.post(url, data) as unknown as ApiResponse<T>;
  return response.data;
}

export async function put<T>(url: string, data?: Record<string, unknown>): Promise<T> {
  const response = await adminInstance.put(url, data) as unknown as ApiResponse<T>;
  return response.data;
}

export async function del<T>(url: string): Promise<T> {
  const response = await adminInstance.delete(url) as unknown as ApiResponse<T>;
  return response.data;
}

// ============================================
// 认证接口
// ============================================

/** 登录响应 */
interface LoginResponse {
  token: string;
  admin: AdminInfo;
}

/** 管理员登录 */
export async function login(params: AdminLoginParams): Promise<LoginResponse> {
  return post<LoginResponse>('/auth/login', params as unknown as Record<string, unknown>);
}

/** 获取当前管理员信息 */
export async function getProfile(): Promise<AdminInfo> {
  const response = await get<{ admin: AdminInfo }>('/auth/me');
  return response.admin;
}

/** 修改密码 */
export async function changePassword(oldPassword: string, newPassword: string): Promise<void> {
  await put('/auth/password', { oldPassword, newPassword });
}

// ============================================
// 作品审核接口
// ============================================

/** 作品列表查询参数 */
export interface WorkListParams {
  status?: string;
  keyword?: string;
  page?: number;
  pageSize?: number;
}

/** 获取作品列表 */
export async function getWorks(params: WorkListParams) {
  return get<{ list: any[]; total: number }>('/works', params as Record<string, unknown>);
}

/** 获取作品详情 */
export async function getWorkDetail(id: number) {
  return get<any>(`/works/${id}`);
}

/** 审核通过 */
export async function approveWork(id: number) {
  return post(`/works/${id}/approve`);
}

/** 审核拒绝 */
export async function rejectWork(id: number, reason: string) {
  return post(`/works/${id}/reject`, { reason });
}

/** 设置精选 */
export async function setWorkFeatured(id: number, featured: boolean) {
  return put(`/works/${id}/featured`, { featured });
}

/** 下架作品 */
export async function offlineWork(id: number, reason: string) {
  return post(`/works/${id}/offline`, { reason });
}

/** 恢复上架作品 */
export async function restoreWork(id: number) {
  return post(`/works/${id}/restore`);
}

// ============================================
// 评论管理接口
// ============================================

/** 获取评论列表 */
export async function getComments(params: { workId?: string; page?: number; pageSize?: number }) {
  return get<{ list: any[]; total: number }>('/comments', params as Record<string, unknown>);
}

/** 删除评论 */
export async function deleteComment(id: string, reason: string) {
  return del(`/comments/${id}?reason=${encodeURIComponent(reason)}`);
}

/** 批量删除评论 */
export async function batchDeleteComments(ids: string[], reason: string) {
  return post('/comments/batch-delete', { ids, reason });
}

// ============================================
// 素材管理接口
// ============================================

/** 获取形象库列表 */
export async function getAvatars(params?: { page?: number; pageSize?: number }) {
  return get<{ list: any[]; total: number }>('/assets/avatars', params as Record<string, unknown>);
}

/** 创建形象 */
export async function createAvatar(data: Record<string, unknown>) {
  return post('/assets/avatars', data);
}

/** 更新形象 */
export async function updateAvatar(id: string | number, data: Record<string, unknown>) {
  return put(`/assets/avatars/${id}`, data);
}

/** 删除形象 */
export async function deleteAvatar(id: string | number) {
  return del(`/assets/avatars/${id}`);
}

/** 获取场景库列表 */
export async function getScenes(params?: { page?: number; pageSize?: number; category?: string; keyword?: string }) {
  return get<{ list: any[]; total: number }>('/assets/scenes', params as Record<string, unknown>);
}

/** 创建场景 */
export async function createScene(data: Record<string, unknown>) {
  return post('/assets/scenes', data);
}

/** 更新场景 */
export async function updateScene(id: string | number, data: Record<string, unknown>) {
  return put(`/assets/scenes/${id}`, data);
}

/** 删除场景 */
export async function deleteScene(id: string | number) {
  return del(`/assets/scenes/${id}`);
}

/** 获取道具库列表 */
export async function getProps(params?: { page?: number; pageSize?: number; category?: string; keyword?: string }) {
  return get<{ list: any[]; total: number }>('/assets/props', params as Record<string, unknown>);
}

/** 创建道具 */
export async function createProp(data: Record<string, unknown>) {
  return post('/assets/props', data);
}

/** 更新道具 */
export async function updateProp(id: string | number, data: Record<string, unknown>) {
  return put(`/assets/props/${id}`, data);
}

/** 删除道具 */
export async function deleteProp(id: string | number) {
  return del(`/assets/props/${id}`);
}

// ============================================
// 模板管理接口
// ============================================

/** 获取音色模板列表 */
export async function getVoices(params?: { page?: number; pageSize?: number; voiceType?: string; keyword?: string; isActive?: string }) {
  return get<{ list: any[]; total: number }>('/templates/voices', params as Record<string, unknown>);
}

/** 创建音色模板 */
export async function createVoice(data: Record<string, unknown>) {
  return post('/templates/voices', data);
}

/** 更新音色模板 */
export async function updateVoice(id: number, data: Record<string, unknown>) {
  return put(`/templates/voices/${id}`, data);
}

/** 删除音色模板 */
export async function deleteVoice(id: number) {
  return del(`/templates/voices/${id}`);
}

/** 获取视觉风格列表 */
export async function getVisualStyles(params?: { page?: number; pageSize?: number; keyword?: string }) {
  return get<{ list: any[]; total: number }>('/templates/styles', params as Record<string, unknown>);
}

/** 创建视觉风格 */
export async function createVisualStyle(data: Record<string, unknown>) {
  return post('/templates/styles', data);
}

/** 更新视觉风格 */
export async function updateVisualStyle(id: string, data: Record<string, unknown>) {
  return put(`/templates/styles/${id}`, data);
}

/** 删除视觉风格 */
export async function deleteVisualStyle(id: string) {
  return del(`/templates/styles/${id}`);
}

/** 获取背景音乐列表 */
export async function getBackgroundMusic(params?: { page?: number; pageSize?: number; category?: string; keyword?: string }) {
  return get<{ list: any[]; total: number }>('/templates/music', params as Record<string, unknown>);
}

/** 创建背景音乐 */
export async function createBackgroundMusic(data: Record<string, unknown>) {
  return post('/templates/music', data);
}

/** 更新背景音乐 */
export async function updateBackgroundMusic(id: string, data: Record<string, unknown>) {
  return put(`/templates/music/${id}`, data);
}

/** 删除背景音乐 */
export async function deleteBackgroundMusic(id: string) {
  return del(`/templates/music/${id}`);
}

/** 获取故事模板列表 */
export async function getStoryTemplates(params?: { page?: number; pageSize?: number; category?: string; keyword?: string }) {
  return get<{ list: any[]; total: number }>('/templates/stories', params as Record<string, unknown>);
}

/** 创建故事模板 */
export async function createStoryTemplate(data: Record<string, unknown>) {
  return post('/templates/stories', data);
}

/** 更新故事模板 */
export async function updateStoryTemplate(id: string, data: Record<string, unknown>) {
  return put(`/templates/stories/${id}`, data);
}

/** 删除故事模板 */
export async function deleteStoryTemplate(id: string) {
  return del(`/templates/stories/${id}`);
}

/** 获取标签列表 */
export async function getTags(params?: { page?: number; pageSize?: number; keyword?: string }) {
  return get<{ list: any[]; total: number }>('/tags', params as Record<string, unknown>);
}

/** 创建标签 */
export async function createTag(data: Record<string, unknown>) {
  return post('/tags', data);
}

/** 更新标签 */
export async function updateTag(id: number, data: Record<string, unknown>) {
  return put(`/tags/${id}`, data);
}

/** 删除标签 */
export async function deleteTag(id: number) {
  return del(`/tags/${id}`);
}

// ============================================
// 数据统计接口
// ============================================

/** 获取今日概览 */
export async function getStatsOverview() {
  return get<{
    newWorks: number;
    pendingReview: number;
    aiCalls: number;
    activeUsers: number;
  }>('/stats/overview');
}

/** 获取作品趋势 */
export async function getWorksTrend(params: { period: string; startDate?: string; endDate?: string }) {
  return get<{ data: { date: string; count: number }[] }>('/stats/works', params as Record<string, unknown>);
}

// ============================================
// 系统配置接口
// ============================================

/** 获取配置列表 */
export async function getConfigs(group?: string) {
  return get<{ list: any[] }>('/configs', group ? { group } : undefined);
}

/** 更新配置 */
export async function updateConfig(key: string, value: string) {
  return put(`/configs/${key}`, { value });
}

// ============================================
// 敏感词管理接口
// ============================================

/** 获取敏感词列表 */
export async function getSensitiveWords(params?: { category?: string; page?: number; pageSize?: number }) {
  return get<{ list: any[]; total: number }>('/sensitive-words', params as Record<string, unknown>);
}

/** 添加敏感词 */
export async function addSensitiveWord(data: { word: string; category: string; level: string }) {
  return post('/sensitive-words', data as Record<string, unknown>);
}

/** 批量导入敏感词 */
export async function importSensitiveWords(words: string[]) {
  return post('/sensitive-words/import', { words });
}

/** 删除敏感词 */
export async function deleteSensitiveWord(id: number) {
  return del(`/sensitive-words/${id}`);
}

// ============================================
// 操作日志接口
// ============================================

/** 获取操作日志 */
export async function getOperationLogs(params: {
  adminId?: string;
  module?: string;
  action?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  pageSize?: number;
}) {
  return get<{ list: any[]; total: number; page: number; pageSize: number }>('/logs', params as Record<string, unknown>);
}

// ============================================
// 文件上传接口
// ============================================

/** 上传素材图片 */
export async function uploadImage(file: File, category: string): Promise<{ url: string; ossPath: string; originalName: string; fileSize: number }> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('category', category);
  
  const response = await adminInstance.post('/upload/image', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  }) as unknown as ApiResponse<{ url: string; ossPath: string; originalName: string; fileSize: number }>;
  
  return response.data;
}

/** 上传音频文件 */
export async function uploadAudio(file: File): Promise<{ url: string; ossPath: string; originalName: string; fileSize: number }> {
  const formData = new FormData();
  formData.append('file', file);
  
  const response = await adminInstance.post('/upload/audio', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  }) as unknown as ApiResponse<{ url: string; ossPath: string; originalName: string; fileSize: number }>;
  
  return response.data;
}

export default adminInstance;


// ============================================
// 任务监控接口
// ============================================

/** 任务数据 */
export interface TaskData {
  taskId: string;
  entityId: string;
  userId: string;
  type: string;
  status: string;
  progress: number;
  message: string;
  error?: string;
  createdAt?: number;
  updatedAt: number;
  typeName: string;
  statusName: string;
  elapsed: number;
  isTimeout: boolean;
  /** 积分消耗数量 */
  creditsCost?: number;
  /** 是否已扣除积分 */
  creditsDeducted?: boolean;
  /** 是否已退还积分 */
  creditsRefunded?: boolean;
  /** 积分退还金额 */
  creditsRefundAmount?: number;
}

/** 任务统计 */
export interface TaskStats {
  total: number;
  pending: number;
  processing: number;
  completed: number;
  failed: number;
  timeout: number;
}

/** 获取任务列表参数 */
export interface GetTasksParams {
  type?: string;
  status?: string;
  page?: number;
  pageSize?: number;
  taskId?: string;   // 任务ID搜索（前缀匹配）
  userId?: string;   // 用户ID筛选（精确匹配）
}

/** 获取任务列表 */
export async function getTasks(params?: GetTasksParams) {
  return get<{ list: TaskData[]; total: number; stats: TaskStats }>('/tasks', params as Record<string, unknown>);
}

/** 获取任务详情 */
export async function getTaskDetail(taskId: string) {
  return get<TaskData & { redisKey: string; timeoutThreshold: number }>(`/tasks/${taskId}`);
}

/** 取消任务 */
export async function cancelTask(taskId: string, reason?: string) {
  return post(`/tasks/${taskId}/cancel`, { reason });
}

/** 删除任务 */
export async function deleteTask(taskId: string) {
  return del(`/tasks/${taskId}`);
}

/** 批量取消超时任务 */
export async function batchCancelTimeoutTasks() {
  return post<{ canceledCount: number }>('/tasks/batch-cancel-timeout');
}

/** 批量取消等待中的任务 */
export async function batchCancelPendingTasks() {
  return post<{ canceledCount: number }>('/tasks/batch-cancel-pending');
}

/** 清理任务记录 */
export async function cleanupTasks(status?: string) {
  return post<{ deletedCount: number }>('/tasks/cleanup', status ? { status } : undefined);
}

// ============================================
// 链路监控接口
// ============================================

export type { MonitorOverview, MonitorTrendResponse, MonitorSoundEffectResponse } from '@shared/domain/adminMonitor';

/** 获取监控总览 */
export async function getMonitorOverview(days = 1) {
  return get<MonitorOverview>('/monitor/overview', { days: String(days) });
}

/** 获取任务趋势（近 N 天） */
export async function getMonitorTrend(days = 7) {
  return get<MonitorTrendResponse>('/monitor/trend', { days: String(days) });
}

/** 获取音效监控趋势 */
export async function getMonitorSoundEffects(days = 7) {
  return get<MonitorSoundEffectResponse>('/monitor/sound-effects', { days: String(days) });
}

/** API 用量按模型明细 */
export interface ApiUsageByModel {
  modelId: string;
  taskType: string;
  taskLabel: string;
  total: number;
  completed: number;
  failed: number;
  successRate: number;
  totalCredits: number;
  avgDurationSec: number | null;
}

/** API 用量按天汇总 */
export interface ApiUsageByDay {
  date: string;
  total: number;
  completed: number;
  failed: number;
  totalCredits: number;
}

export interface ApiUsageQueryParams {
  days?: number;
  startTime?: number;
  endTime?: number;
}

/** 获取 API 用量统计 */
export async function getApiUsage(query: ApiUsageQueryParams = { days: 7 }) {
  const params: Record<string, string> = {};

  if (query.days != null) {
    params.days = String(query.days);
  }

  if (query.startTime != null) {
    params.startTime = String(query.startTime);
  }

  if (query.endTime != null) {
    params.endTime = String(query.endTime);
  }

  return get<{ byModel: ApiUsageByModel[]; byDay: ApiUsageByDay[] }>(
    '/monitor/api-usage',
    params
  );
}

export interface VideoExportOverview {
  total: number;
  completed: number;
  failed: number;
  inProgress: number;
  stale: number;
  successRate: number;
  avgCompletedDurationSec: number | null;
  totalDownloads: number;
  downloadedOutputs: number;
  downloadConversionRate: number;
}

export interface VideoExportTrendItem {
  date: string;
  total: number;
  completed: number;
  failed: number;
  inProgress: number;
  successRate: number;
}

export interface VideoExportFailureReasonItem {
  reason: string;
  count: number;
}

export interface VideoExportResolutionItem {
  resolution: string;
  count: number;
}

export interface VideoExportDiagnosis {
  outputId: string;
  storyId: string;
  status: 'not_started' | 'generating' | 'completed' | 'failed';
  isStale: boolean;
  queueState: 'waiting' | 'delayed' | 'prioritized' | 'waiting-children' | 'active' | 'failed' | 'completed' | 'missing' | 'unknown';
  suggestedAction: 'skip' | 'keep_generating' | 'mark_failed' | 'sync_volc';
  hasVolcJobId: boolean;
}

export interface VideoExportItem {
  id: string;
  storyId: string;
  userId: string;
  internalUserId: string;
  username: string | null;
  nickname: string | null;
  csdUserId: string | null;
  status: 'not_started' | 'generating' | 'completed' | 'failed';
  progress: number;
  resolution: string | null;
  volcJobId: string | null;
  errorMessage: string | null;
  videoUrl: string | null;
  duration: number | null;
  fileSize: number | null;
  createdAt: string;
  updatedAt: string;
  isStale: boolean;
  queueState: VideoExportDiagnosis['queueState'];
  suggestedAction: VideoExportDiagnosis['suggestedAction'];
}

export interface VideoExportDetail extends VideoExportItem {
  diagnosis: VideoExportDiagnosis;
}

export interface VideoExportListResponse {
  list: VideoExportItem[];
  total: number;
  page: number;
  pageSize: number;
}

export interface VideoExportBatchReconcileResult {
  scanned: number;
  completed: number;
  failed: number;
  stillInProgress: number;
  skipped: number;
}

export interface VideoExportListParams {
  page?: number;
  pageSize?: number;
  keyword?: string;
  status?: VideoExportItem['status'];
  staleOnly?: boolean;
  days?: number;
}

export async function getVideoExportOverview(days = 7) {
  return get<VideoExportOverview>('/video-exports/overview', { days: String(days) });
}

export async function getVideoExportTrend(days = 7) {
  return get<VideoExportTrendItem[]>('/video-exports/trend', { days: String(days) });
}

export async function getVideoExportFailureReasons(days = 7, limit = 10) {
  return get<VideoExportFailureReasonItem[]>('/video-exports/failure-reasons', {
    days: String(days),
    limit: String(limit),
  });
}

export async function getVideoExportResolutionDistribution(days = 7, limit = 10) {
  return get<VideoExportResolutionItem[]>('/video-exports/resolution-distribution', {
    days: String(days),
    limit: String(limit),
  });
}

export async function getVideoExports(params: VideoExportListParams) {
  return get<VideoExportListResponse>('/video-exports', params as Record<string, unknown>);
}

export async function getVideoExportDetail(outputId: string) {
  return get<VideoExportDetail>(`/video-exports/${outputId}`);
}

export async function reconcileStaleVideoExports(limit = 50) {
  return post<VideoExportBatchReconcileResult>('/video-exports/reconcile-stale', { limit });
}

export async function reconcileVideoExport(outputId: string) {
  return post<VideoExportDetail>(`/video-exports/${outputId}/reconcile`);
}

export async function markVideoExportFailed(outputId: string, reason?: string) {
  return post<VideoExportDetail>(`/video-exports/${outputId}/mark-failed`, reason ? { reason } : {});
}

// ============================================
// 用户管理接口
// ============================================

/** 用户数据 */
export interface UserData {
  id: string;
  username: string;
  nickname: string | null;
  email: string;
  avatar: string | null;
  status: string;
  projectCount: number;
  createdAt: string;
}

/** 用户项目数据 */
export interface UserProjectData {
  id: string;
  title: string;
  status: string;
  statusLabel: string;
  currentStep: number;
  sceneCount: number;
  coverUrl: string | null;
  videoRatio: string;
  isPublished: boolean;
  createdAt: string;
  updatedAt: string;
}

/** 分镜图片数据 */
export interface SceneImageData {
  id: string;
  url: string;
  thumbnail: string | null;
  isSelected: boolean;
}

/** 分镜视频数据 */
export interface SceneVideoData {
  id: string;
  url: string;
  thumbnailUrl: string | null;
  duration: number;
  isSelected: boolean;
}

/** 分镜配音数据 */
export interface SceneVoiceData {
  id: string;
  audioUrl: string;
  duration: number;
}

/** 分镜数据 */
export interface SceneData {
  id: string;
  orderNum: number;
  description: string;
  dialogue: string | null;
  speakerName: string | null;
  locationName: string | null;
  duration: number;
  shotType: string | null;
  cameraMove: string | null;
  status: string;
  images: SceneImageData[];
  videos: SceneVideoData[];
  voice: SceneVoiceData | null;
}

/** 角色数据 */
export interface CharacterData {
  id: string;
  name: string;
  avatarUrl: string | null;
}

/** 场景数据 */
export interface LocationData {
  id: string;
  name: string;
  imageUrl: string | null;
}

/** 项目详情数据 */
export interface ProjectDetailData {
  story: {
    id: string;
    title: string;
    script: string;
    status: string;
    statusLabel: string;
    currentStep: number;
    videoRatio: string;
    styleId: string | null;
    styleName: string | null;
    isPublished: boolean;
    coverUrl: string | null;
    totalDuration: number | null;
    createdAt: string;
    updatedAt: string;
  };
  scenes: SceneData[];
  characters: CharacterData[];
  locations: LocationData[];
}

export interface GetUsersParams {
  keyword?: string;
  page?: number;
  pageSize?: number;
  projectCountSort?: 'asc' | 'desc';
}

/** 获取用户列表 */
export async function getUsers(params?: GetUsersParams) {
  return get<{ list: UserData[]; total: number }>('/users', params as Record<string, unknown>);
}

/** 获取用户项目列表 */
export async function getUserProjects(userId: string, params?: { status?: string; page?: number; pageSize?: number }) {
  return get<{ user: { id: string; username: string; nickname: string | null }; list: UserProjectData[]; total: number }>(
    `/users/${userId}/projects`,
    params as Record<string, unknown>
  );
}

/** 获取项目详情 */
export async function getUserProjectDetail(userId: string, storyId: string) {
  return get<ProjectDetailData>(`/users/${userId}/projects/${storyId}`);
}
